import React, { useCallback, useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { CircularProgress, ListItemText, Menu, MenuItem } from "@mui/material";
import { getAiSummary } from "../../common/network/Api";
import PrimaryButton from "../../common/ui/PrimaryButton";
import useTheme from "../../common/hooks/useTheme";
import AiSummaryModal from "./AiSummaryModal";
import { hasNoAiSummaryData } from "./aiSummaryAvailability";
import { parseAiAmrSummaryResponse } from "./aiAmrSummaryResponse";
import { AiAmrSummary } from "./aiAmrSummaryTypes";
import { AiProgressSummary } from "./aiProgressSummaryTypes";
import {
  AiBloodProgressSummary,
  parseAiBloodProgressSummaryResponse,
} from "./aiBloodProgressSummaryResponse";
import { parseAiProgressSummaryResponse } from "./aiProgressSummaryResponse";
import { parseAiRmrSummaryResponse } from "./aiRmrSummaryResponse";
import { AiRmrSummary } from "./aiRmrSummaryTypes";
import { parseAiRmrInBodySummaryResponse } from "./aiRmrInBodySummaryResponse";
import { AiRmrInBodySummary } from "./aiRmrInBodySummaryTypes";
import { parseAiSummaryResponse } from "./aiSummaryResponse";
import {
  AI_SUMMARY_OPTIONS,
  AiSummaryKind,
  getAiSummaryOption,
} from "./aiSummaryKinds";
import { AiSummaryResponse } from "./aiSummaryTypes";

type Props = {
  customerId: string | null;
};

const getRequestErrorMessage = (error: unknown): string => {
  if (typeof error !== "object" || error === null) {
    return "The summary request failed. Please try again.";
  }

  const maybeError = error as {
    message?: unknown;
    response?: { data?: unknown };
  };
  const responseData = maybeError.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }

  if (typeof responseData === "object" && responseData !== null) {
    const responseRecord = responseData as Record<string, unknown>;
    const responseMessage =
      responseRecord.detail || responseRecord.message || responseRecord.error;
    if (typeof responseMessage === "string" && responseMessage.trim()) {
      return responseMessage.trim();
    }
  }

  return typeof maybeError.message === "string" && maybeError.message.trim()
    ? maybeError.message.trim()
    : "The summary request failed. Please try again.";
};

const AiSummaryFeature = ({ customerId }: Props) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryKind, setSummaryKind] =
    useState<AiSummaryKind>("health_overview");
  const [healthSummary, setHealthSummary] = useState<AiSummaryResponse | null>(
    null
  );
  const [rmrSummary, setRmrSummary] = useState<AiRmrSummary | null>(null);
  const [rmrInBodySummary, setRmrInBodySummary] =
    useState<AiRmrInBodySummary | null>(null);
  const [amrSummary, setAmrSummary] = useState<AiAmrSummary | null>(null);
  const [progressSummary, setProgressSummary] =
    useState<AiProgressSummary | null>(null);
  const [bloodProgressSummary, setBloodProgressSummary] =
    useState<AiBloodProgressSummary | null>(null);
  const [isNotImplemented, setIsNotImplemented] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateSummary = useCallback(
    async (kind: AiSummaryKind) => {
      const option = getAiSummaryOption(kind);
      setMenuAnchor(null);
      setSummaryKind(kind);
      setIsOpen(true);
      setHealthSummary(null);
      setRmrSummary(null);
      setRmrInBodySummary(null);
      setAmrSummary(null);
      setProgressSummary(null);
      setBloodProgressSummary(null);
      setError(null);
      setHasNoData(false);
      setIsNotImplemented(!option.implemented);

      abortControllerRef.current?.abort();
      abortControllerRef.current = null;

      if (!option.implemented) {
        setIsLoading(false);
        return;
      }

      if (!customerId) {
        setIsLoading(false);
        setError("Select a client before requesting an AI Summary.");
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setIsLoading(true);

      try {
        const response = await getAiSummary(
          customerId,
          kind,
          abortController.signal
        );
        if (abortController.signal.aborted) {
          return;
        }

        if (hasNoAiSummaryData(response)) {
          setHasNoData(true);
          return;
        }

        if (kind === "progress_summary_2") {
          setBloodProgressSummary(
            parseAiBloodProgressSummaryResponse(response)
          );
        } else if (kind === "progress_summary_1") {
          setProgressSummary(parseAiProgressSummaryResponse(response));
        } else if (kind === "amr_summary") {
          setAmrSummary(parseAiAmrSummaryResponse(response));
        } else if (kind === "rmr_inbody_summary") {
          setRmrInBodySummary(parseAiRmrInBodySummaryResponse(response));
        } else if (kind === "rmr_summary") {
          setRmrSummary(parseAiRmrSummaryResponse(response));
        } else {
          setHealthSummary(parseAiSummaryResponse(response));
        }
      } catch (requestError) {
        if (!abortController.signal.aborted) {
          setError(getRequestErrorMessage(requestError));
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [customerId]
  );

  const closeSummary = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    setIsOpen(false);
  }, []);

  const retrySummary = useCallback(() => {
    void generateSummary(summaryKind);
  }, [generateSummary, summaryKind]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  return (
    <>
      <PrimaryButton
        label="AI Summary"
        icon={
          isLoading ? (
            <CircularProgress size={15} color="inherit" aria-hidden="true" />
          ) : (
            <SummarizeOutlinedIcon
              sx={{ fontSize: "16px" }}
              aria-hidden="true"
            />
          )
        }
        aria-label={isLoading ? "Generating AI Summary" : "Open AI Summary"}
        aria-controls={menuAnchor ? "ai-summary-menu" : undefined}
        aria-expanded={menuAnchor ? "true" : undefined}
        aria-haspopup="menu"
        aria-busy={isLoading}
        isDisable={isLoading}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "18px",
              transform: menuAnchor ? "rotate(180deg)" : "none",
              transition: "transform 160ms ease",
            }}
          />
        }
        onClick={(event) => {
          event.stopPropagation();
          setMenuAnchor(event.currentTarget);
        }}
        sx={{
          height: "32px",
          minWidth: "112px",
          padding: "6px 12px",
          flexShrink: 0,
          justifyContent: "center",
          fontSize: "12px",
        }}
      />
      <Menu
        id="ai-summary-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-label": "AI Summary type" }}
        PaperProps={{
          sx: {
            minWidth: "248px",
            mt: "6px",
            p: "4px",
            border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
          },
        }}
      >
        {AI_SUMMARY_OPTIONS.map((option) => (
          <MenuItem
            key={option.kind}
            onClick={(event) => {
              event.stopPropagation();
              void generateSummary(option.kind);
            }}
            sx={{
              minHeight: "48px",
              px: "12px",
              borderRadius: "6px",
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              "&:hover": { backgroundColor: theme.colorTokens.N[30] },
            }}
          >
            <ListItemText
              primary={option.label}
              secondary={option.description}
              primaryTypographyProps={{
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                lineHeight: "18px",
              }}
              secondaryTypographyProps={{
                color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                fontFamily: "var(--font-Regular)",
                fontSize: "10px",
                lineHeight: "15px",
              }}
            />
          </MenuItem>
        ))}
      </Menu>
      <AiSummaryModal
        open={isOpen}
        isLoading={isLoading}
        summaryKind={summaryKind}
        isNotImplemented={isNotImplemented}
        hasNoData={hasNoData}
        error={error}
        healthSummary={healthSummary}
        rmrSummary={rmrSummary}
        rmrInBodySummary={rmrInBodySummary}
        amrSummary={amrSummary}
        progressSummary={progressSummary}
        bloodProgressSummary={bloodProgressSummary}
        onClose={closeSummary}
        onRetry={retrySummary}
      />
    </>
  );
};

export default AiSummaryFeature;
